// Export side of the layout bridge: projects the live dashboard state (the panels
// slice + the per-type panel slices) into the portable LayoutConfigV1 contract.
//
// The flat-panels contract carries panel placement as a verbatim Dockview
// `toJSON` blob (`arrangement`), so this projection only assembles `panels[]`
// from the modules and their slice state — there is no grid/slot translation.
// ai-artifact and transient (drilldown) modules are stripped, mirroring the
// existing persist-layout / sync-panels strip sites.
import { isPlainObject } from "lodash"
import { getPanelIdForModule } from "../helpers/module-renderers"
import { MODULE_TYPES, type DashboardModule } from "../modules/types"
import { DEFAULT_ASSET_VIEW_STATE, type AssetViewRootState, type AssetViewState } from "../store/asset-view-slice"
import { DEFAULT_FACTORS_VIEW_STATE, type FactorsViewRootState, type FactorsViewState } from "../store/factors-view-slice"
import { DEFAULT_GRAPH_STATE, type GraphRootState, type GraphState } from "../store/graph-slice"
import { DEFAULT_KPI_STATE, type KPIRootState, type KPIState } from "../store/kpi-slice"
import type { PanelsRootState } from "../store/panels-slice"
import { extractPortableGrid } from "./portable-grid"
import type {
  AssetViewConfig,
  FactorsConfig,
  GraphConfig,
  KpiConfig,
  LayoutConfigV1,
  Panel,
} from "../contracts/layouts"

// The contract caps panel titles at 120 code points (LayoutConfigV1 schema,
// panelTitle.maxLength). Truncate rather than let a long title 400 the write.
const MAX_PANEL_TITLE_LENGTH = 120

// Structural subset of ReduxState the export needs — lets tests pass a
// configureStore(testReducer) state without widening to the full app state.
export interface DashboardPanelSlicesState {
  dashboard: {
    panels: PanelsRootState
    assetView: AssetViewRootState
    factorsView: FactorsViewRootState
    graph: GraphRootState
    kpi: KPIRootState
  }
}

export interface ExportLayoutConfigResult {
  config: LayoutConfigV1
  /** ai-artifact panels stripped on export (pinned artifacts are user-session entities). */
  aiArtifactCount: number
}

type DashboardSlices = DashboardPanelSlicesState["dashboard"]

function isOpaqueRecord(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value)
}

function buildAssetViewConfig(slot: AssetViewState): AssetViewConfig {
  const viewConfig: AssetViewConfig = {
    view: slot.view,
    selectedTableLevels: [...slot.selectedTableLevels],
    showNotHeld: slot.showNotHeld,
    columnOrder: slot.columnOrder,
    // Portable grid arrangement promoted out of the opaque datagridState; widths stay instance-only.
    ...extractPortableGrid(slot.datagridState),
  }
  // The contract types groupBy as an object; the slice's null default means
  // "no explicit choice" and is expressed by omission.
  if (isOpaqueRecord(slot.groupBy)) {
    viewConfig.groupBy = { ...slot.groupBy }
  }
  return viewConfig
}

function buildKpiConfig(slot: KPIState): KpiConfig {
  // Drop an incomplete/legacy selectedGroup to null — a partial (missing name/type) 400s the strict layout write.
  const group = slot.selectedGroup as { id?: unknown, name?: unknown, type?: unknown } | null
  const selectedGroup = group
    && typeof group.id === "string"
    && typeof group.name === "string"
    && typeof group.type === "string"
    ? { id: group.id, name: group.name, type: group.type }
    : null
  return {
    sectionSettings: { ...slot.sectionSettings },
    selectedGroup,
    selectedTab: slot.selectedTab,
  }
}

function buildGraphConfig(slot: GraphState): GraphConfig {
  return {
    selectedTemplate: slot.selectedTemplate,
    disabledItems: { ...slot.disabledItems },
  }
}

function buildFactorsConfig(slot: FactorsViewState): FactorsConfig {
  // Sort travels semantically via sortMetricId/sortDirection — so the portable
  // grid is order + pin only (no sortModel). The contract carries the grouping
  // dimension id; the resolved {id, label} object is a render-time concern.
  const { orderedFields, pinnedColumns } = extractPortableGrid(slot.datagridState)
  const viewConfig: FactorsConfig = {
    view: slot.view,
    groupBy: slot.groupBy?.id ?? null,
    showNotHeld: slot.showNotHeld,
    selectedPeriodPerformance: slot.selectedPeriodPerformance,
    sortMetricId: slot.sortMetricId,
    sortDirection: slot.sortDirection,
    selectedTableLevels: [...slot.selectedTableLevels],
  }
  if (orderedFields) viewConfig.orderedFields = orderedFields
  if (pinnedColumns) viewConfig.pinnedColumns = pinnedColumns
  return viewConfig
}

function getPanelTitle(module: DashboardModule): string | undefined {
  const { title } = module.config
  if (typeof title !== "string" || title.length === 0) {
    return undefined
  }
  const codePoints = [...title]
  return codePoints.length > MAX_PANEL_TITLE_LENGTH
    ? codePoints.slice(0, MAX_PANEL_TITLE_LENGTH).join("")
    : title
}

// Panel key = module id; viewConfig from the module's slice slot, falling back to
// the slice defaults when the panel never populated a slot. Returns null for
// non-portable types (ai-artifact), which are filtered before this point.
function buildPanelForModule(
  slices: DashboardSlices,
  dashboardId: string,
  module: DashboardModule,
): Panel | null {
  const panelId = getPanelIdForModule(module)
  const title = getPanelTitle(module)
  const base = title !== undefined ? { key: module.id, title } : { key: module.id }
  switch (module.type) {
    case MODULE_TYPES.ASSET_VIEW:
      return {
        ...base,
        moduleType: "asset-view",
        viewConfig: buildAssetViewConfig(slices.assetView[dashboardId]?.[panelId] ?? DEFAULT_ASSET_VIEW_STATE),
      }
    case MODULE_TYPES.KPI:
      return {
        ...base,
        moduleType: "kpi",
        viewConfig: buildKpiConfig(slices.kpi[dashboardId]?.[panelId] ?? DEFAULT_KPI_STATE),
      }
    case MODULE_TYPES.GRAPH:
      return {
        ...base,
        moduleType: "graph",
        viewConfig: buildGraphConfig(slices.graph[dashboardId]?.[panelId] ?? DEFAULT_GRAPH_STATE),
      }
    case MODULE_TYPES.FACTORS:
      return {
        ...base,
        moduleType: "factors",
        viewConfig: buildFactorsConfig(slices.factorsView[dashboardId]?.[panelId] ?? DEFAULT_FACTORS_VIEW_STATE),
      }
    default:
      return null
  }
}

/**
 * Project the dashboard's live state for `dashboardId` into a LayoutConfigV1.
 *
 * `arrangement` is the verbatim Dockview `toJSON()` blob (from
 * `actions.getApi()?.toJSON()`), stored as-is for WYSIWYG sharing — never the
 * debounced, transient-stripped, up-to-500ms-stale `dockviewLayout` preference.
 * Pass null to export a panels-only layout (no placement).
 *
 * Strips ai-artifact panels (pinned artifacts are user-session entities) and
 * transient drilldown modules, mirroring the persist-layout and sync-panels
 * strip sites.
 */
export function exportLayoutConfig(
  state: DashboardPanelSlicesState,
  dashboardId: string,
  arrangement: Record<string, unknown> | null,
): ExportLayoutConfigResult {
  const slices = state.dashboard
  const modules = slices.panels[dashboardId]?.modules ?? []
  const aiArtifactCount = modules.filter((module) => module.type === MODULE_TYPES.AI_ARTIFACT).length

  const panels = modules
    .filter((module) => module.type !== MODULE_TYPES.AI_ARTIFACT && module.config.transient !== true)
    .map((module) => buildPanelForModule(slices, dashboardId, module))
    .filter((panel): panel is Panel => panel !== null)

  const config: LayoutConfigV1 = { schemaVersion: 1, panels }
  if (arrangement) {
    config.arrangement = arrangement
  }

  return { config, aiArtifactCount }
}
